<template>
  <b-container fluid class="no-bottom">
    <div class="bottom-actions scan-bar">
      <b-button v-if="inSaving" variant="outline-secondary" disabled>Enregistrement...</b-button>
      <template v-else>
        <!-- QR-004 : dépannage quand l'étiquette est illisible. La douchette reste le
             mode nominal, sélectionné par défaut. -->
        <ScanModeSwitch v-model="scanMode" class="scan-mode" />
        <!-- QR-006 : en mode photo, le viseur prend la place du champ. Il n'en occupe
             que la hauteur utile, pour laisser voir la liste, et masquer le champ évite
             de faire surgir le clavier du mobile sur un écran où l'on ne tape pas. -->
        <CameraScanner v-if="cameraEntry" @decoded="onCameraDecoded" @close="closeCameraScanner" />
        <b-button-group v-else>
          <input type="text" v-model="internal_code"
            :placeholder="manualEntry ? 'Saisir un code référence' : 'Scanner / ajouter une référence'"
            class="form-control" ref="internal_code" @keyup.enter="submitManualEntry()" />
          <b-button v-if="manualEntry" variant="outline-secondary" :disabled="!internal_code"
            @click="submitManualEntry()" style="border-left: 1px solid #e9e9e9">OK</b-button>
        </b-button-group>
      </template>
    </div>
    <!-- Actions de fin de préparation, séparées de la barre de scan et ancrées au bas de
         l'écran : elles closent le travail fait au-dessus, alors que le champ de scan reste
         en tête, sous la main pendant tout le parcours des produits. -->
    <div class="preparation-actions" v-if="!inSaving && scanned_quantity">
      <b-button-group>
        <b-button variant="outline-secondary" @click="valide()">Valider</b-button>
        <b-button v-if="values.renting.withdrawed_at_agency != true" variant="outline-primary"
          @click="valide(true)">Expédier</b-button>
      </b-button-group>
    </div>
    <b-row>
      <b-col class="d-flex">
        <backlink class="mt-20" />
        <div class="flex-grow-1">
          <h1>
            Commande
            <div v-if="!isBusy" class="subtitle">#{{ values.renting.renting_number }}</div>
          </h1>
        </div>
      </b-col>
    </b-row>
    <div v-if="isBusy" class="loader"></div>
    <p v-if="isBusy" class="text-center">Les données sont en cours de chargement...</p>
    <b-row v-else>
      <b-col>
        <!-- INC-005 : l'entrepôt de retrait est celui choisi par le client. Le badge nommait
             Carquefou en dur, du temps de l'entrepôt unique. -->
        <b-badge v-if="values.renting && values.renting.withdrawed_at_agency == 1" variant="info">
          Enlèvement à l'entrepôt
          <template v-if="values.renting.withdrawal_warehouse_name">
            {{ values.renting.withdrawal_warehouse_code }} - {{ values.renting.withdrawal_warehouse_name }}
          </template>
        </b-badge>

        <template v-if="values.renting.end_renting_date">
          <br><br><strong>Date de fin de location estimée :</strong> {{ values.renting.end_renting_date |
        moment("DD-MM-YYYY") }}
        </template>
        <h2 :class="[values.group_slug, 'clanprobold']">
          <i class="far fa-toolbox"></i>
          {{ ($store.getters["renting_preparation/getGroup"](values.group_slug) || {}).text || values.group_slug }}
        </h2>

        <div class="table-wrapper">
          <b-table ref="table" hover v-if="values.products_renting.length" :fields="product_renting_fields"
            :items="values.products_renting">
            <template slot="product.name" slot-scope="data">
              <a :class="[
        data.item.references && data.item.references.length == data.item.quantity
          ? values.group_slug
          : '',
      ]" href @click.prevent="product_id = data.item.product.id" v-b-modal.modal-product>{{ data.value }}</a>
              <!-- Emplacement du produit dans l'entrepôt de la préparation -->
              <small v-if="storageLabel(data.item.product)" class="d-block text-muted">
                <i class="far fa-map-marker-alt"></i> {{ storageLabel(data.item.product) }}
              </small>
            </template>

            <template slot="product.id" slot-scope="data">
              <b-badge v-if="data.value" :variant="data.value <= 0
        ? 'danger'
        : data.value <= data.item.product.minimum_stock
          ? 'warning'
          : 'success'
        ">{{ data.value }}</b-badge>
            </template>


            <!-- Quantité scannée et stock réel en pastilles ; le stock ouvre sa répartition par entrepôt -->
            <template slot="scanned_quantity" slot-scope="data">
              <b-badge :variant="scannedVariant(data.item)">{{ scannedCount(data.item) }}</b-badge>
            </template>
            <template slot="product.stock" slot-scope="data">
              <a href class="stock-link" @click.prevent="openStockByWarehouse(data.item)">
                <b-badge :variant="stockVariant(data.item)">{{ data.item.product.stock }} <i class="far fa-chevron-down"></i></b-badge>
              </a>
            </template>

            <template slot="checked" slot-scope="data">
              <b-badge v-if="data.item.references && data.item.references.length == data.item.quantity"
                :variant="values.group_slug" class="circle">
                <i class="fa fa-check"></i>
              </b-badge>
            </template>

            <template slot="row-details" slot-scope="row">
              <b-card>
                <b-row v-for="(reference, index) in row.item.references" :key="index"
                  @click="reference_id = reference_id == reference.id ? null : reference.id">
                  <b-col class="ref_delete text-center" cols="1" v-if="reference_id == reference.id"
                    @click="deleteRef(row.item.id, reference.id)">
                    <i class="far fa-trash-alt"></i>
                  </b-col>
                  <b-col class="ref_number text-center" cols="2">{{ index + 1 }}</b-col>
                  <b-col class="ref_code">{{ reference.internal_code }}</b-col>
                </b-row>
              </b-card>
            </template>
          </b-table>
          <b-table ref="table" hover v-if="values.products_selling.products.length" :fields="product_selling_fields"
            :items="values.products_selling.products">
            <template slot="product.name" slot-scope="data">
              <a :class="[
        data.item.scanned_quantity && data.item.scanned_quantity == data.item.quantity
          ? values.group_slug
          : '',
      ]" href @click.prevent="product_id = data.item.product.id" v-b-modal.modal-product>{{ data.value }}</a>
              <!-- Emplacement du produit dans l'entrepôt de la préparation -->
              <small v-if="storageLabel(data.item.product)" class="d-block text-muted">
                <i class="far fa-map-marker-alt"></i> {{ storageLabel(data.item.product) }}
              </small>
            </template>

            <template slot="product.id" slot-scope="data">
              <b-badge v-if="data.value" :variant="data.value <= 0
        ? 'danger'
        : data.value <= data.item.product.stock_mini
          ? 'warning'
          : 'success'
        ">{{ data.value }}</b-badge>
            </template>

            <!-- Quantité scannée et stock réel en pastilles ; le stock ouvre sa répartition par entrepôt -->
            <template slot="scanned_quantity" slot-scope="data">
              <b-badge :variant="scannedVariant(data.item)">{{ scannedCount(data.item) }}</b-badge>
            </template>
            <template slot="product.stock" slot-scope="data">
              <a href class="stock-link" @click.prevent="openStockByWarehouse(data.item)">
                <b-badge :variant="stockVariant(data.item)">{{ data.item.product.stock }} <i class="far fa-chevron-down"></i></b-badge>
              </a>
            </template>

            <template slot="checked" slot-scope="data">
              <b-badge v-if="data.item.scanned_quantity && data.item.scanned_quantity == data.item.quantity
        " :variant="values.group_slug" class="circle">
                <i class="fa fa-check"></i>
              </b-badge>
            </template>
          </b-table>
          <template v-if="values.products_selling.lots &&
        values.products_selling.lots != {}">
            <div v-for="(lot, index) in values.products_selling.lots" :key="index" class="lot m-sm p-sm bg-info">
              <h3 class="text-muted m-b-sm">Lot : {{ lot.name }}</h3>
              <b-table ref="table" hover v-if="lot.products.length" :fields="product_selling_fields"
                :items="lot.products">
                <template slot="product.name" slot-scope="data">
                  <a :class="[
        data.item.scanned_quantity && data.item.scanned_quantity == data.item.quantity
          ? values.group_slug
          : '',
      ]" href @click.prevent="product_id = data.item.product.id" v-b-modal.modal-product>{{
        data.value
      }}</a>
              <!-- Emplacement du produit dans l'entrepôt de la préparation -->
              <small v-if="storageLabel(data.item.product)" class="d-block text-muted">
                <i class="far fa-map-marker-alt"></i> {{ storageLabel(data.item.product) }}
              </small>
                </template>

                <template slot="product.id" slot-scope="data">
                  <b-badge v-if="data.value" :variant="data.value <= 0
        ? 'danger'
        : data.value <= data.item.product.stock_mini
          ? 'warning'
          : 'success'
        ">{{ data.value }}</b-badge>
                </template>

                <!-- Quantité scannée et stock réel en pastilles ; le stock ouvre sa répartition par entrepôt -->
                <template slot="scanned_quantity" slot-scope="data">
                  <b-badge :variant="scannedVariant(data.item)">{{ scannedCount(data.item) }}</b-badge>
                </template>
                <template slot="product.stock" slot-scope="data">
                  <a href class="stock-link" @click.prevent="openStockByWarehouse(data.item)">
                    <b-badge :variant="stockVariant(data.item)">{{ data.item.product.stock }} <i class="far fa-chevron-down"></i></b-badge>
                  </a>
                </template>

                <template slot="checked" slot-scope="data">
                  <b-badge v-if="data.item.scanned_quantity && data.item.scanned_quantity == data.item.quantity
        " :variant="values.group_slug" class="circle">
                    <i class="fa fa-check"></i>
                  </b-badge>
                </template>
              </b-table>
            </div>
          </template>
        </div>
      </b-col>
    </b-row>

    <b-modal id="modal-product" hide-footer centered no-close-on-backdrop v-if="product_id">
      <template slot="modal-title">Produit</template>
      <ProductMobileDetail :product_id="product_id"></ProductMobileDetail>
    </b-modal>

    <b-modal id="modal-quantity" lazy hide-footer centered no-close-on-backdrop >
      <template slot="modal-title">Quantité à scanner</template>
      <h3 v-if="scanned_product">{{scanned_product.name}}</h3>
      <p v-if="scanned_product"><strong>Quantité déjà scannée :</strong> {{scanned_quantity_by_product[scanned_product.id]}}</p>
      <p v-if="scanned_product"><strong>Quantité demandée :</strong> {{asked_quantity_by_product[scanned_product.id]}}</p>
      <p><strong>Quantité à scanner :</strong></p>
      <input v-if="scanned_product" class="form-control" type="number" name="quantity_to_scan" ref="quantity_to_scan" :value="asked_quantity_by_product[scanned_product.id]-scanned_quantity_by_product[scanned_product.id]" :max="asked_quantity_by_product[scanned_product.id]-scanned_quantity_by_product[scanned_product.id]" min="1">
      <p class="error" v-if="quantity_error">La quantité scannée est non conforme.</p>
      <div class="d-flex d-flex--justify-between actions-container">
        <b-button variant="outline-primary" @click="scan_one()">Scanner 1</b-button>
        <b-button variant="primary" @click="scan_all()" >Scanner la quantité ci-dessus</b-button>
      </div>
    </b-modal>
    <!-- Stock du produit par entrepôt, avec l'emplacement où il est rangé -->
    <b-modal id="modal-stock-warehouse" hide-footer centered>
      <template slot="modal-title"><i class="far fa-warehouse"></i> Stock par entrepôt</template>
      <template v-if="stock_product">
        <p class="stock-product">
          {{ stock_product.name }}
          <span v-if="stock_product.internal_code" class="stock-code">{{ stock_product.internal_code }}</span>
        </p>
        <ul v-if="stock_rows.length" class="stock-warehouses">
          <li v-for="row in stock_rows" :key="row.warehouse_id || 'none'" class="stock-warehouses__item">
            <div class="stock-warehouses__head">
              <span>{{ row.warehouse_name }}</span>
              <span v-if="row.warehouse_code" class="stock-code">{{ row.warehouse_code }}</span>
              <b-badge class="ml-auto" :variant="row.available > 0 ? 'success' : 'danger'">{{ row.available }} dispo</b-badge>
            </div>
            <div class="stock-warehouses__place">
              <i class="far fa-map-marker-alt"></i>
              <template v-if="row.storage_environment_name">
                {{ row.storage_zone_name }} <i class="far fa-chevron-right stock-warehouses__sep"></i>
                <strong>{{ row.storage_environment_name }}</strong>
              </template>
              <em v-else class="text-muted">Emplacement non défini</em>
            </div>
          </li>
        </ul>
        <p v-else class="text-muted">Aucun stock pour ce produit dans vos entrepôts.</p>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import ProductMobileDetail from "@/components/ProductMobile/ProductMobileDetail";
import scannedValueMixin from "@/mixins/scannedValue";
import { environmentLabel } from "@/components/StorageEnvironment/labels";

export default {
  name: "RentingPreparationMobileDetail",
  mixins: [scannedValueMixin],
  components: {
    ProductMobileDetail,
  },
  data() {
    return {
      product_renting_fields: [
        {
          key: "product.name",
          label: "Produit à la location",
        },
        {
          key: "quantity",
          label: "Qte",
        },
        {
          key: "scanned_quantity",
          label: "Scanné",
        },
        {
          key: "product.stock",
          label: "Stock réel",
        },
        {
          key: "checked",
          label: "",
        },
      ],
      product_selling_fields: [
        {
          key: "product.name",
          label: "Produit à la vente",
        },
        {
          key: "quantity",
          label: "Qte",
        },
        {
          key: "scanned_quantity",
          label: "Scanné",
        },
        {
          key: "product.stock",
          label: "Stock réel",
        },
        {
          key: "checked",
          label: "",
        },
      ],
      values: {},
      internal_code: null,
      asked_quantity: 0,
      asked_quantity_by_product: {},
      scanned_quantity_by_product: {},
      scanned_quantity: 0,
      product_id: null,
      // Fenêtre « Stock par entrepôt »
      stock_product: null,
      stock_rows: [],
      reference_id: null,
      isBusy: true,
      inSaving: false,
      inScanning: false,
      scanned_product: null,
      quantity_error: false,
    };
  },
  mounted() {
    this.isBusy = true;
    this.$store
      .dispatch("renting_preparation/getItem", {
        id: this.$route.params.id,
        wait: true,
      })
      .then(() => {
        this.values = _.cloneDeep(
          this.$store.getters["renting_preparation/getItem"](this.$route.params.id)
        );

        // Test si le contenu demandé est bien trouvé
        let self = this;
        if (Object.entries(this.values).length <= 0) {
          this.$toastr.error(
            "La fiche demandée n'a pas été trouvée. Assure-vous que l'URL saisie soit correcte."
          );
          setTimeout(function () {
            self.$router.go(-1);
          }, 5000);
          return;
        }

        // On calcul le nombre de références et produit à scanner
        this.values.products_renting.forEach((item) => {
          this.asked_quantity = this.asked_quantity + item.quantity;
        });
        this.values.products_selling.forEach((item) => {
          this.asked_quantity = this.asked_quantity + item.quantity;
          this.asked_quantity_by_product[item.product.id] = item.quantity;
          this.scanned_quantity_by_product[item.product.id] = 0;
        });

        // Tri par code interne
        this.values.products_renting.sort((a, b) => a.product.internal_code.localeCompare(b.product.internal_code))
        this.values.products_selling.sort((a, b) => a.product.internal_code.localeCompare(b.product.internal_code))

        // On ajoute les références déjà présentes (dans le cas d'une édition)
        this.values.unique_references.forEach((reference) => {
          this.addReference(reference);
        });

        // On considère que tous les produits sont déjà scanné (dans le cas d'une édition)
        this.values.products_selling = this.values.products_selling.map((item) => ({
          ...item,
          scanned_quantity: item.prepared == true ? item.quantity : 0,
        }));


        // Regroupement des lots
        let products_selling_updated = {
          products: [],
          lots    : {}
        }
        this.values.products_selling.map(product_selling => {
          if (product_selling.lot != null) {
            if (products_selling_updated.lots.hasOwnProperty(product_selling.lot) !== true) {
              products_selling_updated.lots[product_selling.lot] = {
                product_id: product_selling.parent_product.id,
                name      : product_selling.parent_product.name,
                products  : []
              }
            }
            products_selling_updated.lots[product_selling.lot].products.push(product_selling)
          } else {
            products_selling_updated.products.push(product_selling)
          }
        })
        this.values.products_selling = products_selling_updated

        this.refresh_scanned();

        this.isBusy = false;
      });
    this.$store.dispatch("product/getAllForSelect");
    this.$refs.internal_code.focus();
  },
  watch: {
    internal_code: function (internal_code) {
      // QR-004 : en saisie manuelle, la validation est explicite (bouton / Entrée).
      if (internal_code && !this.manualEntry) {;
        // La douchette tape caractère par caractère : on attend la fin de la salve.
        this.handleScannedValueDebounced(internal_code);
      }
    },
  },
  methods: {
    scannedCount(item) {
      return item.references ? item.references.length : item.scanned_quantity || 0;
    },
    // Vert : tout est scanné ; orange : en cours ; gris : rien encore.
    scannedVariant(item) {
      const scanned = this.scannedCount(item);
      return scanned >= item.quantity && scanned > 0 ? "success" : scanned > 0 ? "warning" : "info";
    },
    // Rouge : rupture ; orange : moins que la quantité demandée ; vert sinon.
    stockVariant(item) {
      const stock = item.product.stock || 0;
      return stock <= 0 ? "danger" : stock < item.quantity ? "warning" : "success";
    },
    openStockByWarehouse(item) {
      this.stock_product = item.product;
      this.stock_rows = item.product.stock_by_warehouse || [];
      this.$root.$emit("bv::show::modal", "modal-stock-warehouse");
    },
    storageLabel(product) {
      return product && product.storage_environment ? environmentLabel(product.storage_environment, false) : null;
    },
    // QR-003 : appelé par le mixin une fois la valeur normalisée.
    onScanResolved: function (code) {
      this.getReferenceOrProduct(code);
    },
    /**
     * Cherche une référence ou un produit à la vente à partir de son code barre
     * @param   {string|interger} internal_code  Code barre
     * @returns                             Si les tests sont passés, appelle la méthode addRéférence
     */
    getReferenceOrProduct: function (internal_code) {
      if (this.inScanning == true) {
        this.internal_code = null;
        return
      }

      this.inScanning = true

      // On recherche un produit à la vente
      var product = this.$store.getters["product/getItemByBarCode"](internal_code);
      if (product) {

        if (product.stock > 0) {
          let renting_preparation_product
          if (this.values.products_selling.products) {
            renting_preparation_product = this.values.products_selling.products.find(
              (item) => item.product.id == product.id
            );
          }
          // Si le produit correspond bien à un produit demandé
          if (renting_preparation_product) {
            // et qu'on a pas déjà ajouté le nombre de produit demandé, on l'ajoute à la préparation
            if (
              !renting_preparation_product.scanned_quantity ||
              renting_preparation_product.scanned_quantity < renting_preparation_product.quantity
            ) {
              this.scanned_product = product;
              this.$root.$emit("bv::show::modal", "modal-quantity");
            } else {
              this.inScanning = false
              this.$toastr.error(
                "Vous avez déjà scanné le nombre de produit demandé pour ce produit : " +
                  renting_preparation_product.name
              );
            }
          } else { // Sinon on va parcourir les lots pour recherche le produit dedans
            let product_found        = false
            let product_all_prepared = true
            for (const lot in this.values.products_selling.lots) {
              this.values.products_selling.lots[lot].products.forEach((item) => {
                if (item.product.id == product.id) {
                  product_found = item.product.name
                  if (!item.hasOwnProperty("prepared") || item.prepared == false) {
                    product_all_prepared = false
                  }
                }
              })
            }

            if (product_found === false) {
              this.inScanning = false
              this.$toastr.error("Le produit scanné ne correspond à aucun produit demandé");
            } else if(product_all_prepared == true) {
              this.inScanning = false
              this.$toastr.error(
                "Vous avez déjà scanné le nombre de produit demandé pour ce produit : " +
                product.name
              );
            } else {
              this.scanned_product = product;
              this.$root.$emit("bv::show::modal", "modal-quantity");
            }
          }
        } else {
          this.inScanning = false
          this.$toastr.error(
            "Le produit scanné n'est pas en stock. Si c'est une erreur, veuillez corriger son statut depuis un ordinateur"
          );
        }
      } else {
        // Si aucun produit n'a été trouvé, on recherche une référence
        this.$store
          .dispatch("reference/isAvailable", internal_code)
          .then((response) => {
            let reference = {
              ...response,
              internal_code: internal_code,
            };
            if (reference.avalaible == true) {

              let renting_preparation_product = this.values.products_renting.find(
                (item) => item.product.id == reference.product.id
              );
              // Si la référence correspond bien à un matériel demandé
              if (renting_preparation_product) {
                // et qu'on a pas déjà ajouté le nombre de références demandé, on l'ajoute à la préparation
                if (
                  !renting_preparation_product.references ||
                  renting_preparation_product.references.length <
                    renting_preparation_product.quantity
                ) {

                  // Si la référence a un étalonnage à faire avant la fin de la loc, on demande confirmation
                  if (reference.nextEspectedMaintenanceDate && this.values.group_slug == 'instruments' && this.values.renting.agency_im_control === true && this.values.renting.end_renting_date && this.$moment(reference.nextEspectedMaintenanceDate).format("YYYY-MM-DD") < this.$moment(this.values.renting.end_renting_date).format("YYYY-MM-DD")) {
                    if (confirm( "Le produit sélectionné doit être révisé avant la date de fin de location. Êtes-vous certain(e) de vouloir l'expédier ?" )) {
                      this.addReference(reference);
                    }
                  } else {
                    this.addReference(reference);
                  }
                } else {
                  this.inScanning = false
                  this.$toastr.error(
                    "Vous avez déjà scanné le nombre de référence demandé pour ce matériel : " +
                      renting_preparation_product.product.name
                  );
                }
              } else {
                this.inScanning = false
                this.$toastr.error("La référence scannée ne correspond à aucun matériel demandé");
              }
            } else {
              this.inScanning = false
              this.$toastr.error(
                "La référence scannée n'est pas en stock. Si c'est une erreur, veuillez corriger son statut depuis un ordinateur"
              );
            }
          })
          .catch((error) => {
            this.inScanning = false
            this.$toastr.error(
              "Le code barre scanné ne correspond à aucune référence ou produit à la vente"
            );
          });
      }

      this.internal_code = null;
    },
    /**
     * Ajoute une référence unique à la préparation
     * @param   {object}  reference   Référence
     */
    addReference: function (reference) {
      // On modifie les données de la préparation courante pour faire apparaitre les références
      // dans la liste des matériels
      let renting_preparation_product = this.values.products_renting.find(
        (item) => item.product.id == reference.product.id
      );

      // On s'assure que la ref unique correspond bien à une ref matériel demandée
      if (!renting_preparation_product) {
        this.inScanning = false
        this.$toastr.error(
          "La référence scannée ne correspond à aucun matériel présent dans cette commande"
        );
        return;
      }
      if (!renting_preparation_product.hasOwnProperty("references")) {
        renting_preparation_product.references = [];
      }

      // On vérifie que la ref unique n'a pas déjà été scannée
      var scanned_references = renting_preparation_product.references.map(
        (item) => item.internal_code
      );
      if (scanned_references.includes(reference.internal_code)) {
        this.inScanning = false
        this.$toastr.error("Cette référence a déjà été scannée pour cette commande");
        return;
      }
      renting_preparation_product.references.push(reference);
      renting_preparation_product._showDetails = true;

      if (!renting_preparation_product.hasOwnProperty("scanned_quantity")) {
        renting_preparation_product.scanned_quantity = 0;
      }

      renting_preparation_product.scanned_quantity = renting_preparation_product.references.length;

      this.refresh_scanned();

      this.inScanning = false
      this.$toastr.success("La référence a bien été ajoutée à la préparation");
    },
    /**
     * Ajoute un produit à la vente à la préparation
     * @param   {object}  product   Produit à la vente
     */
    addProduct: function (product, quantity) {

      // On modifie les données de la préparation courante pour mettre à jour le nombre de produit scannés
      let renting_preparation_product = this.values.products_selling.products.find(
        (item) => item.product.id == product.id
      );

      // On s'assure que le produit correspond bien à un produit demandé
      if (renting_preparation_product) {
        if (!renting_preparation_product.hasOwnProperty("scanned_quantity")) {
          renting_preparation_product.scanned_quantity = 0;
        }

        renting_preparation_product.scanned_quantity += quantity;
        this.scanned_quantity_by_product[product.id] += quantity;

        renting_preparation_product.prepared =
          renting_preparation_product.quantity == renting_preparation_product.scanned_quantity;

        this.$root.$emit("bv::hide::modal", "modal-quantity");

      } else { // Sinon on va parcourir les lots pour recherche le produit dedans

        let product_found  = false
        let product_setted = false
        for (const lot in this.values.products_selling.lots) {
          this.values.products_selling.lots[lot].products.forEach((item) => {
            if (item.product.id == product.id) {
              product_found = true
              if ((!item.hasOwnProperty("prepared") || item.prepared == false) && product_setted == false) {
                if (!item.hasOwnProperty("scanned_quantity")) {
                  item.scanned_quantity = 0;
                }
                item.scanned_quantity += quantity;
                item.prepared = item.quantity == item.scanned_quantity;
                product_setted = true // Permet d'ignorer les prochains lots avec le même produit
              }
            }
          })
        }

        this.$root.$emit("bv::hide::modal", "modal-quantity");

        if (product_found === false) {
          this.inScanning = false
          this.$toastr.error(
            "Le produit scanné ne correspond à aucun produit présent dans cette commande"
          );
          return;
        }
      }

      this.refresh_scanned();
      this.inScanning = false
      this.$toastr.success("Le produit a bien été ajouté à la préparation");
    },
    /**
     * Retire une référence unique de la préparation
     * @param   {integer}   product_id          ID du produit
     * @param   {integer}   reference_id        ID de la référence unique
     */
    deleteRef: function (products_renting_id, reference_id) {
      if (products_renting_id && reference_id) {
        let renting_preparation_product = this.values.products_renting.find(
          (item) => item.id == products_renting_id
        );

        renting_preparation_product.references = renting_preparation_product.references.filter(
          (item) => item.id != reference_id
        );

        renting_preparation_product.scanned_quantity = renting_preparation_product.references.length;

        this.refresh_scanned();

        this.$toastr.success("La référence a bien été retirée de la préparation");
      }
    },
    /**
     * Calcul et met à jour l'état complété ou non de la préparation
     */
    refresh_scanned: function () {
      this.scanned_quantity = 0;
      this.values.products_renting.forEach((item) => {
        this.scanned_quantity = item.references
          ? this.scanned_quantity + item.references.length
          : this.scanned_quantity;
      });
      if (this.values.products_selling.hasOwnProperty('products')) {
        this.values.products_selling.products.forEach((item) => {
          this.scanned_quantity = item.scanned_quantity
            ? this.scanned_quantity + item.scanned_quantity
            : this.scanned_quantity;
        });
      }
      for (const lot in this.values.products_selling.lots) {
        this.values.products_selling.lots[lot].products.forEach((item) => {
          this.scanned_quantity = item.scanned_quantity
            ? this.scanned_quantity + item.scanned_quantity
            : this.scanned_quantity;
        });
      }
    },
    scan_one: function (){
      this.inScanning = false;
      this.quantity_error = false;
      if(1 <= (this.asked_quantity_by_product[this.scanned_product.id] - this.scanned_quantity_by_product[this.scanned_product.id])){
        this.addProduct(this.scanned_product, 1);
      }else{
        this.quantity_error = true;
      }
    },
    scan_all: function(){
      this.inScanning = false;
      this.quantity_error = false;
      if(parseInt(this.$refs.quantity_to_scan.value) <= (this.asked_quantity_by_product[this.scanned_product.id] - this.scanned_quantity_by_product[this.scanned_product.id])){
        this.addProduct(this.scanned_product, parseInt(this.$refs.quantity_to_scan.value));
      }else{
        this.quantity_error = true;
      }
    },
    /**
     * Valide la préparation
     * @param   {boolean}   send    Si 'true', redirige ensuite vers la création d'un shipping pour
     *                              cette préparation
     */
    valide: function (send) {
      // Si la certaines références ou produit n'ont pas été scannée, alors on demande confirmation
      if (
        this.scanned_quantity < this.asked_quantity &&
        !confirm(
          "Une ou plusieurs références demandées n’ont pas été ajoutées. Êtes-vous certain de vouloir valider cette préparation ?"
        )
      ) {
        return;
      }

      this.inSaving = true;

      var unique_references = [];
      this.values.products_renting.forEach((item) => {
        if (item.hasOwnProperty("references")) {
          item.references.forEach((reference) => {
            unique_references.push(reference.id);
          });
        }
      });

      // Décomposition des lots
      let products_selling = []
      this.values.products_selling.products.forEach((item) => {
        products_selling.push(item)
      })
      for (const lot in this.values.products_selling.lots) {
        this.values.products_selling.lots[lot].products.forEach((item) => {
          products_selling.push(item)
        })
      }

      let renting_preparation = {
        ...this.values,
        renting_id: this.values.renting.id,
        // Si c'est un retrait à l'agence, on passe directement en expédiée
        status_slug: this.values.renting.withdrawed_at_agency
          ? "renting-preparation-expediee"
          : "renting-preparation-prete-pour-expedition",
        unique_references: unique_references,
        products_selling: products_selling,
        completed: this.scanned_quantity == this.asked_quantity,
        planning: false,
      };


      this.$store.dispatch("renting_preparation/saveItem", renting_preparation).then((response) => {
        this.inSaving = false;
        // Si l'utilisateur demande l'expédition, on le renvoit vers l'édition de l'expédition créée automatiquement
        if (send && response.data.object && response.data.object.shippings.length) {
          let last_shipping = response.data.object.shippings.pop();
          this.$router.push({
            name: "ShippingMobileEditReferer",
            params: { id: last_shipping.id, referer: "renting_preparation" },
          });
        }
        // Sinon retour à la liste
        else {
          this.$router.push({ name: "RentingPreparationMobileList" });
        }
      });
    },
  },
};
</script>

<style lang="scss">
// Feuille volontairement non scopée : elle doit atteindre l'intérieur des <b-table>, dont le
// markup est produit par bootstrap-vue et ne porte pas l'attribut de scope du composant. Tout
// est donc préfixé par la classe de page (`$route.name`, posée sur `.wrapper-content` par
// App.vue) pour que rien ne déteigne sur les autres écrans une fois la feuille injectée.
.wrapper-content.RentingPreparationMobileDetail {
  // Réserve la hauteur de la barre d'actions ancrée en bas, sans quoi les dernières lignes de
  // produits passent dessous et deviennent illisibles en fin de scan.
  padding-bottom: 90px;

  .bottom-actions {
    position: sticky;
    top: 0;
    z-index: 10;
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.05);
  }

  // Barre des actions de fin de préparation (valider / expédier).
  //
  // Ancrée au bas de la fenêtre : ces boutons concluent le scan et doivent rester atteignables
  // sans remonter, quelle que soit la longueur de la liste de produits. Elle est distincte de
  // `.bottom-actions`, qui porte le champ de scan et reste en tête — les deux ne servent pas au
  // même moment du travail.
  //
  // Décalée de la largeur du menu latéral en desktop ; la media query mobile la remet pleine
  // largeur, le menu y étant escamoté.
  .preparation-actions {
    position: fixed;
    bottom: 0;
    left: 240px;
    width: calc(100% - 240px);
    z-index: 1000;
    background-color: #fff;
    box-shadow: 0 -8px 15px 0 rgba(0, 0, 0, 0.05);
  }
}

// ----------------------------------------------------------------------------------------
// Mobile (<= 768px) : rendu tactile d'origine, inchangé.
// ----------------------------------------------------------------------------------------
@media (max-width: 768px) {
  .wrapper-content.RentingPreparationMobileDetail .preparation-actions {
    left: 0;
    width: 100%;

    .btn-group {
      width: 100%;

      .btn {
        padding: 20px 0;
        border-radius: 0;
        height: auto;
        font-size: 12px;
        width: 100%;
        border: none;
        border-right: 1px solid #e9e9e9;

        &:last-child {
          border-right: none;
        }
      }
    }
  }
}

// ----------------------------------------------------------------------------------------
// Desktop (>= 769px) : même écran, mais piloté à la souris sur une page large. Les blocs
// pleine largeur pensés pour le pouce y deviennent des aplats vides et éloignent chaque
// quantité de son produit. On rend à chacun sa largeur utile.
// ----------------------------------------------------------------------------------------
@media (min-width: 769px) {
  .wrapper-content.RentingPreparationMobileDetail {
    // Champ de scan : un input de 1 500 px pour un code référence n'aide personne. Encart
    // compact, mode de saisie ramené à gauche du champ plutôt qu'empilé au-dessus.
    .bottom-actions.scan-bar {
      display: flex;
      align-items: center;
      max-width: 640px;
      padding: 12px 20px;
      background-color: #fff;

      .scan-mode {
        flex: 0 0 auto;
        margin: 0 20px 0 0;
      }

      .btn-group {
        flex: 1 1 auto;
      }

      input {
        margin: 0;
        padding: 8px 12px;
        border: 1px solid #e9e9e9;
      }
    }

    // Largeur de lecture des listes de produits : au-delà, l'oeil traverse un vide entre le
    // nom et les quantités qui le concernent.
    .table-wrapper {
      max-width: 1100px;

      table.table {
        th,
        td {
          vertical-align: middle;
        }

        // Colonnes chiffrées (Qte, Scanné, Stock réel) : largeur fixe et centrées, comme
        // `.authorization-table` du thème. Le nom de produit garde tout le reste.
        th:not(:first-child),
        td:not(:first-child) {
          width: 110px;
          text-align: center;
        }

        // Coche de complétude : une simple pastille, pas besoin d'une colonne pleine.
        th:last-child,
        td:last-child {
          width: 60px;
        }
      }

      // Références scannées, dépliées sous la ligne produit : une colonne de codes étalée sur
      // toute la largeur ne se lit pas. Le survol signale qu'un clic ouvre la suppression.
      .card-body .row {
        max-width: 380px;
        cursor: pointer;

        &:hover {
          background-color: #f7f7f7;
        }
      }
    }

    // Fin de préparation : à la souris, deux boutons de 700 px n'ajoutent rien. Barre
    // discrète, boutons à la taille du thème (40 px), alignés à droite comme partout ailleurs.
    .preparation-actions {
      display: flex;
      justify-content: flex-end;
      padding: 15px 25px;
      border-top: 1px solid #e9e9e9;
      box-shadow: none;
    }
  }
}
</style>

<style lang="scss" scoped>
// Stock réel cliquable et fenêtre « Stock par entrepôt »
.stock-link .badge i {
  margin-left: 4px;
  font-size: 8px;
}

.stock-product {
  font-size: 13px;
  margin-bottom: 15px;
}

.stock-code {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  background-color: #ecf1f5;
  color: #999;
  font-size: 9px;
  vertical-align: middle;
}

.stock-warehouses {
  list-style: none;
  margin: 0;
  padding: 0;

  &__item {
    padding: 12px 0;
    border-bottom: solid 1px #e9e9e9;

    &:last-child {
      border-bottom: none;
    }
  }

  &__head {
    display: flex;
    align-items: center;
    font-size: 12px;
    margin-bottom: 4px;
  }

  &__place {
    font-size: 13px;

    > .fa-map-marker-alt {
      color: #1aae7c;
      margin-right: 4px;
    }
  }

  &__sep {
    color: #c1c1c1;
    font-size: 9px;
    margin: 0 4px;
  }
}

@import "~@/assets/scss/_variables.scss";

.outillage {
  color: $outillage;
}

.instruments {
  color: $instruments;
}

.service_et_logistique {
  color: $service_et_logistique;
}

table {
  .ref_delete {
    background-color: #ed6376;
    color: white;
  }

  .ref_number {
    font-size: 11px;
    @extend .clanprobold;
    color: $text-30p;
  }

  .ref_code {
    font-size: 13px;
  }
}

// Encadrée plutôt que posée en surplomb, comme sur le retour mobile.
// Reste collée en haut au défilement : c'est le repère du préparateur qui scanne.
.bottom-actions.scan-bar {
  box-shadow: none;
  border: 1px solid #eeeeee;
  margin-bottom: 15px;
}

.bottom-actions {
  input {
    border-radius: 0;
    border: none;
    padding: 5px;
    margin: 20px 25px;
    @extend .clanprobook;
  }

  // Le champ de scan porte déjà la marge basse : le sélecteur de mode se cale
  // sur son retrait horizontal sans en rajouter.
  .scan-mode {
    margin: 20px 25px 0;
  }
}

.d-flex{
  &--justify-between{ justify-content: space-between; }
}

.actions-container{ margin-top: 20px; }

.error{ color: $red; font-weight: bold; margin-top: 10px; }
</style>



// WEBPACK FOOTER //
// src/components/RentingPreparationMobile/RentingPreparationMobileDetail.vue